import React, { useState, useCallback, useRef, useEffect, ReactElement } from 'react';
import { Button, ReyPopper, Dialog } from '@framework-controls/core';
import { IconDashMenu, IconViewInspResults, IconViewHistory, CheckIn, IconCalendar, IconRemove } from 'src/content/icons';
import { Customer, ServiceDocsData, RepairOrder, VehicleRecs, VehicleRec, TokenKeys, ROHistory } from 'src/models';
import { IRepairOrdersState } from 'src/store/states';
import { strings } from 'src/localization';
import classNames from 'classnames';
import VehicleModelImages from '../VehicleImages/VehicleModelImages';
import { checkSpecTheme } from 'src/helpers/OEMHelper/OEMHelper';
import Logo from '../Logo/Logo';
import * as css from './MenuServiceDocs.module.scss';
import moment from 'moment';
import 'moment/min/locales.min';

interface IProps {
    customer?: Customer;
    repairOrders?: IRepairOrdersState;
    loadVin?: string;
    loadRO?: string;
    previewFlag: string;
    serviceDocsSpecs?: ServiceDocsData;
    theme: string;
    vehicles: VehicleRecs;
    selectedVehicle: string;
    newTabUrl?: string;
    store: string;
    branch: string;
    locale: string;
    roHistory: ROHistory[];
    roHistoryBuilt: boolean;
    portalLaunched: string;
    eApprovalLaunched: string;
    showMenu: boolean;
    Itype: string | undefined;
    generateDocsUrl: (tokenData: TokenKeys) => void;
    clearNewTabUrl: () => void;
}

const MenuServiceDocs = (props: IProps) => {
    const [menuOpenFlag, setMenuOpenFlag] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(document.createElement('div'));
    const [selectedRO, setSelectedRO] = useState<RepairOrder | undefined>(undefined);
    const [recentRODate, setRecentRODate] = useState<string | undefined>('');
    const [mileageIn, setMileageIn] = useState<number | undefined>(0);
    const [iconText, setIconText] = useState<string>('');
    const [startUrlGen, setStartUrlGen] = useState<boolean>(true);
    const [checkRepairUrl, setCheckRepairUrl] = useState<string>('');
    const [scheduleServiceUrl, setScheduleServiceUrl] = useState<string>('');
    const [viewServiceHistUrl, setViewServiceHistUrl] = useState<string>('');
    const [noHistoryOpen, setNoHistoryOpen] = useState<boolean>(false);

    useEffect(() => {
        if (props.customer && props.repairOrders && props.repairOrders.currentRepairOrder) {
            let repairOrders = props.repairOrders.RepairOrders;
            // Filter repair orders and save the date of the most recent.
            if (repairOrders.length > 1) {
                if (props.loadVin) {
                    repairOrders = repairOrders.filter(ro => ro.vin === props.loadVin);
                }
                const date = Math.max(...repairOrders.map(ro => Date.parse(ro.RODate)));
                setRecentRODate(repairOrders.find(ro => Date.parse(ro.RODate) === date)?.RODate);
            } else if (repairOrders[0]) {
                setRecentRODate(repairOrders[0].RODate);
            }
            repairOrders = props.repairOrders.RepairOrders;
            // Filter repair orders and select the most recent unless we received an RO in token.
            if (props.loadRO) {
                setSelectedRO(repairOrders.find(ro => ro.RONum === props.loadRO));
            } else {
                if (repairOrders.length > 1) {
                    if (props.loadVin) {
                        repairOrders = repairOrders.filter(ro => ro.vin === props.loadVin);
                    }
                    // this grabs most recent RO
                    const date = Math.max(...repairOrders.map(ro => Date.parse(ro.RODate)));
                    setSelectedRO(repairOrders.find(ro => Date.parse(ro.RODate) === date));
                } else {
                    setSelectedRO(repairOrders[0]);
                }
            }
            setMileageIn(selectedRO?.ROOdometer);
        }
    }, [props.loadRO, props.customer, props.repairOrders, props.repairOrders?.currentRepairOrder, props.loadVin,
        selectedRO?.ROOdometer]);

    useEffect(() => {
        if (selectedRO) {
            const pendingItemsCount = selectedRO.eApprovalSentOrViewedCount ?? 0;
            if (pendingItemsCount > 0 && !selectedRO.eApprovalRedItems) {
                setIconText(pendingItemsCount.toString());
            }
            if (pendingItemsCount > 0 && selectedRO.eApprovalRedItems) {
                setIconText(strings.ExclamationPoint);
            }
        }
    }, [selectedRO]);

    // generate the urls for the menu buttons
    useEffect(() => {
        if (startUrlGen && selectedRO && props.customer?.nameId && props.loadVin && props.serviceDocsSpecs &&
            checkRepairUrl==='' && scheduleServiceUrl === '' && viewServiceHistUrl === '') {
            setStartUrlGen(false);
            const tokenData: TokenKeys = {
                keyStore: props.store,
                keyBranch: props.branch,
                keyProfile: props.serviceDocsSpecs.osdProfile,
                keyNameId: props.customer?.nameId,
                keyVin: props.loadVin,
                keyRo: selectedRO.RONum,
                keyServiceDocsFlag: '',
                keyCheckRepairStatusFlag: 'Y',
                keyExpireDays: '60',
            };
            props.generateDocsUrl(tokenData);
        }
    }, [selectedRO,
        checkRepairUrl,
        scheduleServiceUrl,
        viewServiceHistUrl,
        startUrlGen,
        props.store,
        props.branch,
        props.customer,
        props.loadVin,
        props.serviceDocsSpecs,
        props.generateDocsUrl]);

    useEffect(() => {
        if (selectedRO && checkRepairUrl==='' && props.newTabUrl) {
            setCheckRepairUrl(props.newTabUrl);
            props.clearNewTabUrl();
        }
    }, [selectedRO, checkRepairUrl, props.customer, props.loadVin, props.newTabUrl, props.clearNewTabUrl]);

    useEffect(() => {
        if (selectedRO && scheduleServiceUrl === '' && checkRepairUrl && viewServiceHistUrl === '' && props.serviceDocsSpecs) {
            const tokenData: TokenKeys = {
                keyStore: props.store,
                keyBranch: props.branch,
                keyProfile: props.serviceDocsSpecs.osdProfile,
                keyNameId: props.customer?.nameId,
                keyVin: props.loadVin,
                keyServiceDocsFlag: '',
                keyExpireDays: '60',
            };
            props.generateDocsUrl(tokenData);
        }
    }, [selectedRO,
        checkRepairUrl,
        scheduleServiceUrl,
        viewServiceHistUrl,
        props.store,
        props.branch,
        props.customer,
        props.loadVin,
        props.serviceDocsSpecs,
        props.generateDocsUrl]);

    useEffect(() => {
        if (selectedRO && checkRepairUrl !=='' && scheduleServiceUrl==='' && viewServiceHistUrl==='' && props.newTabUrl) {
            setScheduleServiceUrl(props.newTabUrl);
            props.clearNewTabUrl();
        }
    }, [selectedRO,
        checkRepairUrl,
        scheduleServiceUrl,
        viewServiceHistUrl,
        props.customer,
        props.loadVin,
        props.newTabUrl,
        props.clearNewTabUrl,
    ]);

    useEffect(() => {
        if (selectedRO && checkRepairUrl && scheduleServiceUrl && viewServiceHistUrl==='' && props.serviceDocsSpecs) {
            const tokenData: TokenKeys = {
                keyStore: props.store,
                keyBranch: props.branch,
                keyProfile: props.serviceDocsSpecs.osdProfile,
                keyNameId: props.customer?.nameId,
                keyVin: props.loadVin,
                keyServiceDocsFlag: '',
                keyViewServiceHistFlag: 'Y',
                keyExpireDays: '60',
            };
            props.generateDocsUrl(tokenData);
        }
    }, [selectedRO,
        checkRepairUrl,
        scheduleServiceUrl,
        viewServiceHistUrl,
        props.store,
        props.branch,
        props.customer,
        props.loadVin,
        props.serviceDocsSpecs,
        props.generateDocsUrl]);

    useEffect(() => {
        if (selectedRO && checkRepairUrl!=='' && scheduleServiceUrl!=='' && viewServiceHistUrl==='' && props.newTabUrl) {
            setViewServiceHistUrl(props.newTabUrl);
            props.clearNewTabUrl();
        }
    }, [selectedRO,
        checkRepairUrl,
        scheduleServiceUrl,
        viewServiceHistUrl,
        props.customer,
        props.loadVin,
        props.newTabUrl,
        props.clearNewTabUrl,
    ]);

    const menuOpenHandler = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setMenuOpenFlag(menuOpenFlag => !menuOpenFlag);
        setAnchorEl(event.currentTarget);
    }, []);

    const dealerName = props.serviceDocsSpecs?.dealerName;
    let dealerNameArea: ReactElement = <h1 className={css.dealerNameLit}>{dealerName}</h1>;
    if (dealerName && (dealerName.length > 12)) {
        dealerNameArea = <h1 className={css.dealerNameLitSmallFontSize}>{dealerName}</h1>;
    }

    let vehicle: VehicleRec | undefined = undefined;

    let vehMake = '';
    if (props.customer?.vehicleList) {
        vehicle = props.customer?.vehicleList[props.selectedVehicle];
        vehMake = vehicle?.make ?? '';
        if (vehicle?.makeCode === '2I') {
            vehMake = 'SCION';
        }
    }

    let yearStyle = '';
    if (vehicle) {
        // string for the h6 value
        if (vehicle.year) {
            yearStyle = yearStyle + vehicle.year;
        }
        if (vehicle.color) {
            if (vehicle.year) {
                yearStyle = yearStyle + ' | ' + vehicle.color;
            } else {
                yearStyle = yearStyle + vehicle.color;
            }
        }
        if (vehicle.licenseNo) {
            if (yearStyle !== '') {
                yearStyle = yearStyle + ' | ' + vehicle.licenseNo;
            } else {
                yearStyle = yearStyle + vehicle.licenseNo;
            }
        }
        // if (vehicle.style) {
        //     if (vehicle.year) {
        //         yearStyle = yearStyle + ' | ' + vehicle.style;
        //     } else {
        //         yearStyle = yearStyle + vehicle.style;
        //     }
        // }
    }

    let vehicleSection: ReactElement | undefined = undefined;

    if (vehicle) {
        let roDate: string = '';
        let lastServiced = '';
        if (recentRODate) {
            const processingDate = new Date(recentRODate);
            let processingUTCDate = moment.utc(processingDate);
            processingUTCDate = processingUTCDate.locale(props.locale);
            roDate = processingUTCDate.format('MMMM D, YYYY');
        }
        // put strings in locale
        const miOrKm = props.locale === 'en-US' ? 'mi' : 'km';
        // lastServiced = strings.LastServiced + ' - ' + roDate + ' | ' + vehicle.prevMileage + miOrKm;
        lastServiced = roDate + ' | ' + vehicle.prevMileage + miOrKm;

        // use mileageIn for previous mileage if set
        if (mileageIn !== 0) {
            lastServiced = roDate + ' | ' + mileageIn?.toLocaleString() + miOrKm;
        }

        /* let vehDivAltFlag = false; */
        let vehDivPriFlag = false;
        /* if (props.theme === 'acura') {
            vehDivAltFlag = true;
        } */
        if (props.theme === 'audi' || props.theme === 'mini' || props.theme === 'acura' || props.theme === 'acura-CA') {
            vehDivPriFlag = true;
        }
        const vehicleDataClassName = classNames(
            css.vehicleData,
            {
                [css.vehicleDataContrast]: vehDivPriFlag,
                [css.vehicleDataHonda]: props.theme === 'honda',
                [css.vehicleDataBMW]: props.theme === 'bmw',
                [css.vehicleDataFord]: props.theme === 'ford',


            },
        );
        const makeDivClassName = classNames(
            css.makeDiv,
            {
                [css.makeDivContrast]: vehDivPriFlag,
            },
        );
        vehicleSection = <div className={css.menuVehicle}>
            <VehicleModelImages
                year={vehicle.year}
                make={vehMake}
                model={vehicle.model}
                type='thumb'
                className={css.vehicleImg}
                Itype={props?.Itype}
            />
            <div className={vehicleDataClassName}>
                <div className={makeDivClassName}>
                    {vehMake} {vehicle.model}
                </div>
                <div>{yearStyle}</div>
                <div className={css.lastService}>
                    {strings.LastServiced + ' - '}
                    <div>
                        {lastServiced}
                    </div>
                </div>
            </div>
        </div>;
    }

    // Refs are always null in react test renderer, so in order to properly test we need to set a default ref value
    const initialValue: HTMLDivElement = document.createElement('div');
    const showButtonsRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(initialValue);

    const handleClickOutside = useCallback((e: any) => {
        if (!menuRef.current?.contains(e.target as Element) && !showButtonsRef.current?.contains(e.target as Element)) {
            setMenuOpenFlag(false);
        }
    }, [menuRef, showButtonsRef]);

    useEffect(() => {
        if (menuOpenFlag) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpenFlag, handleClickOutside]);

    const viewInspectHandler = useCallback(() => {
        // opens a new tab to the eApproval site.
        if (selectedRO?.eApprovalLink) {
            const docUrl = selectedRO.eApprovalLink;
            window.open(docUrl);
            setMenuOpenFlag(false);
        }
    }, [selectedRO?.eApprovalLink]);

    const checkRepairHandler = useCallback(() => {
        // opens a new tab to the Service Portal site on a page with the current open RO viewed here.
        if (checkRepairUrl) {
            let docUrl = checkRepairUrl;
            const urlString = window.location;
            const localURL = urlString.host ?? '';
            if (localURL === 'localhost:3000') {
                const localToken = docUrl.split('token').pop();
                docUrl = 'http://localhost:3000/?token'+localToken;
            }
            window.open(docUrl);
            setMenuOpenFlag(false);
        }
    }, [checkRepairUrl]);

    const schueduleServiceHandler = useCallback(() => {
        // opens a new tab to the Service Portal site on a page where the user can schedule a new appointment for viewed vehicle.
        if (scheduleServiceUrl) {
            let docUrl = scheduleServiceUrl;
            const urlString = window.location;
            const localURL = urlString.host ?? '';
            if (localURL === 'localhost:3000') {
                const localToken = docUrl.split('token').pop();
                docUrl = 'http://localhost:3000/?token'+localToken;
            }
            window.open(docUrl);
            setMenuOpenFlag(false);
        }
    }, [scheduleServiceUrl]);

    const serviceHistoryHandler = useCallback(() => {
        // opens a new tab to the hosted pdf for the customer's vehicle service history.
        // if we don't have history display the no history message
        if (props.roHistory.length === 0 && props.roHistoryBuilt) {
            setNoHistoryOpen(true);

            return;
        }
        if (viewServiceHistUrl) {
            // If the viewServiceHistUrl is the scheduleServiceUrl, then the viewServiceHistUrl hasn't been generated yet.
            // Reset the viewServiceHistUrl and return.
            if (viewServiceHistUrl === scheduleServiceUrl) {
                setViewServiceHistUrl('');

                return;
            }
            let docUrl = viewServiceHistUrl;
            const urlString = window.location;
            const localURL = urlString.host ?? '';
            if (localURL === 'localhost:3000') {
                const localToken = docUrl.split('token').pop();
                docUrl = 'http://localhost:3000/?token'+localToken;
            }
            window.open(docUrl);
            setMenuOpenFlag(false);
        }
    }, [viewServiceHistUrl, props.roHistory.length, props.roHistoryBuilt, scheduleServiceUrl]);

    const menuClassName = classNames(
        css.menuToggle,
        {
            [css.menuToggleCheck]: menuOpenFlag,
        },
    );

    const iconTextClasses = classNames(
        css.iconText,
        {
            [css.iconTextCritical]: selectedRO?.eApprovalRedItems,
        },
    );

    const theme = props.theme === 'colorSensitive' ? props.theme : checkSpecTheme(props.serviceDocsSpecs?.theme ?? '');
    let logo = <Logo
        serviceDocsData={props.serviceDocsSpecs}
        theme={theme}
        menuLogo />;
    if (props.serviceDocsSpecs?.logoURL) {
        logo = <img
            className={css.logo}
            src={props.serviceDocsSpecs?.logoURL}
            alt={strings.DealerLogo}
            title={strings.DealerLogo}></img>;
    }

    const listCloseBtnStyles = classNames(
        css.listCloseBtn,
        {
            [css.listCloseBtnBMW]: props.theme === 'bmw',
            [css.listCloseBtnFord]: props.theme === 'ford',

        },
    );

    const menuBtnContentsStyles = classNames(
        css.menuBtnContents,
        {
            [css.menuBtnContentsFord]: props.theme === 'ford',
        },
    );

    return (
        <div>
            <div className={css.menu}>
                <div className={css.logoArea}>
                    {logo}
                </div>
                { props.showMenu && <label className={css.menuBtn}>
                    <span className={menuClassName} ref={menuRef}>
                        <Button
                            id='menuBtn'
                            onClick={menuOpenHandler}
                            type='contained'
                            variant='menuBtn'
                            tooltip={strings.MenuTitle}
                        >
                            <div className={menuBtnContentsStyles}>
                                <div className={css.menuIcon}>
                                    <IconDashMenu />
                                </div>
                                <div className={css.menuLit}>
                                    {strings.Menu}
                                </div>
                            </div>
                        </Button>
                    </span>
                </label>}
                <div className={css.dealerName}>
                    {dealerNameArea}
                </div>
                {/* Vehicle */}
                {vehicleSection}
                {/* Display flyout */}
                {menuOpenFlag ?
                    <ReyPopper
                        className={css.menuPane}
                        transition
                        placement='top-start'
                        anchorEl={anchorEl}
                        isOpen={menuOpenFlag}
                    >
                        <div className={css.menuContent} ref={showButtonsRef}>
                            <div className={css.btnContainer}>
                                {/* View Inspection Results */}
                                {/* Present if eApproval is launched else hide */}
                                {/* If RO has pending eApproval items then active */}
                                {/* If no pending eApproval items then inactive */}
                                {
                                    props.eApprovalLaunched === 'Y' ?
                                        <Button
                                            id='ViewInspect'
                                            onClick={viewInspectHandler}
                                            type={'custom'}
                                            disabled={iconText==='' || props.previewFlag === 'p'}
                                        >
                                            <div className={css.listBtn}>
                                                <div className={css.alertBell}>
                                                    <span className={iconTextClasses}>{iconText}</span>
                                                    <IconViewInspResults className={iconText==='' || props.previewFlag === 'p'?
                                                        css.listIconsDisabled:css.listIcons}/>
                                                </div>
                                                <div className={iconText==='' || props.previewFlag === 'p' ?
                                                    css.listBtnTextDisabled:css.listBtnText}>
                                                    {strings.MenuViewInspectionResults}
                                                </div>
                                            </div>
                                        </Button>: null
                                }
                                {/* Check Repair Status */}
                                {/* if ro closed then disable but show */}
                                {/* if portal is launched show else hide  */}
                                {
                                    props.portalLaunched === 'Y' && props.serviceDocsSpecs &&
                                    !props.serviceDocsSpecs.osdFordSmarttFlag ?
                                        <Button
                                            id='CheckRepairStatus'
                                            onClick={checkRepairHandler}
                                            // is -1 closed?
                                            disabled={props.customer?.isCompany || props.previewFlag === 'p' ||
                                            (selectedRO?.postFlag !== undefined)}
                                        >
                                            <div className={css.listBtn}>
                                                <CheckIn className=
                                                    {props.customer?.isCompany ||
                                                        props.previewFlag === 'p' || (selectedRO?.postFlag !== undefined) ?
                                                        css.listIconsDisabled:css.listIcons}/>
                                                <div className=
                                                    {props.customer?.isCompany ||
                                                        props.previewFlag === 'p' || (selectedRO?.postFlag !== undefined) ?
                                                        css.listBtnTextDisabled:css.listBtnText}>
                                                    {strings.CheckRepairStatus}
                                                </div>
                                            </div>
                                        </Button> : null
                                }
                                {/* Schedule Service */}
                                {/* If portal is launched show else hide */}
                                {
                                    props.portalLaunched === 'Y' ?
                                        <Button
                                            id='ScheduleService'
                                            onClick={schueduleServiceHandler}
                                            disabled={props.customer?.isCompany || props.previewFlag === 'p'}
                                        >
                                            <div className={css.listBtn}>
                                                <IconCalendar className={props.customer?.isCompany || props.previewFlag === 'p'?
                                                    css.listIconsDisabled:css.listIcons}/>
                                                <div className={props.customer?.isCompany || props.previewFlag === 'p' ?
                                                    css.listBtnTextDisabled:css.listBtnText}>
                                                    {strings.ScheduleService}
                                                </div>
                                            </div>
                                        </Button>: null
                                }
                                {/* View Vehicle Service History */}
                                {/* if docs specs has include svc hist checked then show else hide */}
                                {
                                    props.serviceDocsSpecs?.includeHistory ?
                                        <Button
                                            id='ServiceHistory'
                                            onClick={serviceHistoryHandler}
                                            // disabled={props.previewFlag === 'p'}
                                        >
                                            <div className={css.listBtn}>
                                                <IconViewHistory className={css.listIcons}/>
                                                <div className={css.listBtnText}>
                                                    {strings.ViewVehicleServiceHistory}
                                                </div>
                                            </div>
                                        </Button> : null
                                }
                                {/* Close */}
                                <Button
                                    id='CloseBtn'
                                    onClick={() => setMenuOpenFlag(false)}
                                >
                                    <div className={listCloseBtnStyles}>
                                        {strings.Close}
                                    </div>
                                </Button>
                            </div>
                        </div>
                    </ReyPopper>: null}
            </div >
            <Dialog
                id='RefreshMsg'
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={noHistoryOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={''}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => setNoHistoryOpen(false)}
                onBackDropClick={() => setNoHistoryOpen(false)}
                width='full'
            >
                <div className={css.headerbox}>
                    {strings.ROHistory}
                    <div className={css.headerIcon}>
                        <IconRemove id='closeBtn' onClick={() => setNoHistoryOpen(false)}className={css.closeBtn}/>
                    </div>
                </div>
                <div>{strings.VehNoRepair}</div>
            </Dialog>
        </div>
    );
};

export default MenuServiceDocs;